/* ============================================================
   plch-hero-thumbs — carrousel (desktop)
   - Viewport en overflow-x : scroll natif (scrollTo), **sans** transform sur la piste
   - Pas de clones : navigation modulo + centrage par scroll (fiable)
   - Passage dernière ↔ première : scroll **instantané** pour éviter de traverser toute la rangée
   ============================================================ */

(function () {
  'use strict';

  const SECTION = '.plch-hero-thumbs';
  const ITEM = '.plch-hero-thumbs__item';
  const LINK = '.plch-hero-thumbs__link';
  const IMG_NEUTRAL = '.plch-hero-thumbs__img--neutral';
  const TILT = '.plch-hero-thumbs__tilt';
  const FRAME = '.plch-hero-thumbs__frame';
  const VIEWPORT = '.plch-hero-thumbs__viewport';
  const TRACK = '.plch-hero-thumbs__track';
  const NAV_PREV = '.plch-hero-thumbs__nav--prev';
  const NAV_NEXT = '.plch-hero-thumbs__nav--next';

  const TILT_MAX_DEG = 8;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isFinePointer = () =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function circularDist(k, active, n) {
    if (n <= 1) return k === active ? 0 : 2;
    const d = Math.abs(k - active);
    return Math.min(d, n - d);
  }

  function setDistTier(items, activeIndex) {
    const n = items.length;
    items.forEach((item, k) => {
      const d = circularDist(k, activeIndex, n);
      const tier = d === 0 ? 0 : d === 1 ? 1 : 2;
      item.dataset.plchDist = String(tier);
    });
  }

  /**
   * Centre une vignette dans le viewport via scroll (pas de translate sur la piste).
   * @param {boolean} instant — true : pas d’animation (extrémités « en boucle » ou reduced motion)
   */
  function scrollItemToCenter(viewport, item, instant) {
    if (!viewport || !item) return;

    const v = viewport.getBoundingClientRect();
    const el = item.getBoundingClientRect();
    const vpCenter = v.left + v.width / 2;
    const elCenter = el.left + el.width / 2;
    const delta = elCenter - vpCenter;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    let left = viewport.scrollLeft + delta;
    left = Math.max(0, Math.min(maxScroll, left));

    const useInstant = instant || prefersReducedMotion();

    if (useInstant) {
      viewport.scrollLeft = left;
    } else {
      try {
        viewport.scrollTo({ left, behavior: 'smooth' });
      } catch (e) {
        viewport.scrollLeft = left;
      }
    }
  }

  const tiltHandlers = new WeakMap();
  const hoverSwapHandlers = new WeakMap();

  function detachAllTilts(section) {
    const fn = tiltHandlers.get(section);
    if (fn) {
      fn();
      tiltHandlers.delete(section);
    }
  }

  function attachTiltToLink(link) {
    const tilt = link.querySelector(TILT);
    if (!tilt) return null;

    let rafId = null;

    const onMove = (event) => {
      const rect = link.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (event.clientX - cx) / (rect.width / 2);
      const dy = (event.clientY - cy) / (rect.height / 2);
      const ry = Math.max(-1, Math.min(1, dx)) * TILT_MAX_DEG;
      const rx = Math.max(-1, Math.min(1, dy)) * TILT_MAX_DEG * -1;

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        tilt.style.setProperty('--plch-tilt-x', rx.toFixed(2) + 'deg');
        tilt.style.setProperty('--plch-tilt-y', ry.toFixed(2) + 'deg');
      });
    };

    const onLeave = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        tilt.style.setProperty('--plch-tilt-x', '0deg');
        tilt.style.setProperty('--plch-tilt-y', '0deg');
      });
    };

    link.addEventListener('mousemove', onMove);
    link.addEventListener('mouseleave', onLeave);

    return () => {
      link.removeEventListener('mousemove', onMove);
      link.removeEventListener('mouseleave', onLeave);
      if (rafId) cancelAnimationFrame(rafId);
      tilt.style.removeProperty('--plch-tilt-x');
      tilt.style.removeProperty('--plch-tilt-y');
    };
  }

  function attachAllTilts(section, items) {
    detachAllTilts(section);
    if (!isFinePointer() || prefersReducedMotion()) return;

    const cleanups = [];
    items.forEach((item) => {
      const link = item.querySelector(LINK);
      if (!link) return;
      const un = attachTiltToLink(link);
      if (un) cleanups.push(un);
    });

    if (cleanups.length > 0) {
      tiltHandlers.set(section, () => {
        cleanups.forEach((c) => c());
      });
    }
  }

  function detachHoverSwap(section) {
    const fn = hoverSwapHandlers.get(section);
    if (fn) {
      fn();
      hoverSwapHandlers.delete(section);
    }
  }

  function attachHoverSwapToLink(link) {
    const neutral = link.querySelector(IMG_NEUTRAL);
    if (!neutral || !link.dataset.plchHoverSrc) return null;

    const neutralAttrs = {
      src: neutral.getAttribute('src'),
      srcset: neutral.getAttribute('srcset'),
      sizes: neutral.getAttribute('sizes'),
    };
    const hoverAttrs = {
      src: link.dataset.plchHoverSrc,
      srcset: link.dataset.plchHoverSrcset,
      sizes: link.dataset.plchHoverSizes,
    };

    if (!hoverAttrs.src && !hoverAttrs.srcset) return null;

    const applyImageAttrs = (attrs) => {
      if (attrs.srcset) {
        neutral.setAttribute('srcset', attrs.srcset);
      } else {
        neutral.removeAttribute('srcset');
      }

      if (attrs.sizes) {
        neutral.setAttribute('sizes', attrs.sizes);
      } else {
        neutral.removeAttribute('sizes');
      }

      if (attrs.src) {
        neutral.setAttribute('src', attrs.src);
      }
    };

    const showHover = () => {
      applyImageAttrs(hoverAttrs);
    };

    const showNeutral = () => {
      applyImageAttrs(neutralAttrs);
    };

    // Etat initial explicite pour éviter un rendu ambigu au premier hover.
    showNeutral();

    link.addEventListener('mouseenter', showHover);
    link.addEventListener('mouseleave', showNeutral);
    link.addEventListener('focusin', showHover);
    link.addEventListener('focusout', showNeutral);

    return () => {
      showNeutral();
      link.removeEventListener('mouseenter', showHover);
      link.removeEventListener('mouseleave', showNeutral);
      link.removeEventListener('focusin', showHover);
      link.removeEventListener('focusout', showNeutral);
    };
  }

  function attachAllHoverSwaps(section, items) {
    detachHoverSwap(section);
    const cleanups = [];
    items.forEach((item) => {
      const link = item.querySelector(LINK);
      if (!link) return;
      const un = attachHoverSwapToLink(link);
      if (un) cleanups.push(un);
    });
    if (cleanups.length > 0) {
      hoverSwapHandlers.set(section, () => {
        cleanups.forEach((c) => c());
      });
    }
  }

  function updateCarousel(section, activeIndex, items) {
    const n = items.length;
    if (n === 0) return;

    const safeActive = ((activeIndex % n) + n) % n;
    section.dataset.plchActive = String(safeActive);

    setDistTier(items, safeActive);

    items.forEach((item, k) => {
      item.classList.toggle('is-active', k === safeActive);

      const link = item.querySelector(LINK);
      if (link) {
        if (k === safeActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      }
    });
  }

  function playWave(section) {
    if (prefersReducedMotion()) return;
    if (section.dataset.waved === '1') return;
    section.dataset.waved = '1';
    section.classList.add('is-waking');
  }

  function setupWaveObserver(section) {
    if (!('IntersectionObserver' in window)) {
      playWave(section);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playWave(section);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(section);
  }

  function initSection(section) {
    const viewport = section.querySelector(VIEWPORT);
    const track = section.querySelector(TRACK);
    if (!viewport || !track) return;

    const items = Array.from(track.querySelectorAll(ITEM));
    const n = items.length;
    if (n === 0) return;

    const parsed = parseInt(section.dataset.plchInitialActive || '0', 10);
    let active = Number.isFinite(parsed) ? parsed : 0;
    active = Math.min(Math.max(0, active), n - 1);

    section.style.setProperty('--plch-thumb-scale-active', n >= 5 ? '1.12' : '1.08');

    function applyTrackLayout() {
      scrollItemToCenter(viewport, items[active], true);
    }

    function setActive(nextRaw) {
      const next = ((nextRaw % n) + n) % n;
      const prev = active;
      if (next === prev) return;

      const jumpEnds =
        n > 1 &&
        ((prev === n - 1 && next === 0) || (prev === 0 && next === n - 1));

      active = next;
      updateCarousel(section, active, items);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollItemToCenter(viewport, items[active], jumpEnds);
        });
      });
    }

    updateCarousel(section, active, items);
    attachAllTilts(section, items);
    attachAllHoverSwaps(section, items);

    requestAnimationFrame(() => {
      requestAnimationFrame(applyTrackLayout);
    });

    if ('ResizeObserver' in window) {
      let roDeb = null;
      const ro = new ResizeObserver(() => {
        clearTimeout(roDeb);
        roDeb = window.setTimeout(applyTrackLayout, 80);
      });
      ro.observe(viewport);
    } else {
      let resizeT = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeT);
        resizeT = window.setTimeout(applyTrackLayout, 100);
      });
    }

    window.addEventListener('load', applyTrackLayout, { once: true });

    const prevBtn = section.querySelector(NAV_PREV);
    const nextBtn = section.querySelector(NAV_NEXT);

    const onNav = (delta) => (event) => {
      event.stopPropagation();
      setActive(active + delta);
    };

    if (prevBtn) prevBtn.addEventListener('click', onNav(-1));
    if (nextBtn) nextBtn.addEventListener('click', onNav(1));

    const frame = section.querySelector(FRAME);
    if (frame) {
      frame.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setActive(active - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          setActive(active + 1);
        }
      });
    }

    setupWaveObserver(section);
  }

  function init() {
    document.querySelectorAll(SECTION).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
